import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  TextField,
  Chip,
  CircularProgress,
  Typography,
  Paper,
  IconButton,
  Stack,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getCategories, getCategoryTree } from '../../../app/api';
import {
  normalizeCategory,
  normalizeCategoryTreeNode,
  buildCategoryLabelMap,
  getCategoryBreadcrumbLabel,
} from './categoryUtils';
import { useTranslation } from '../../../app/LanguageContext';

const SEARCH_DEBOUNCE_MS = 250;

function SortableCategoryChip({ category, displayLabel, onRemove, disabled }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <Box ref={setNodeRef} style={style} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, maxWidth: '100%' }}>
      <IconButton
        size="small"
        disabled={disabled}
        {...attributes}
        {...listeners}
        sx={{ mt: 0.25, flexShrink: 0 }}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Chip
        label={displayLabel}
        title={displayLabel}
        onDelete={disabled ? undefined : () => onRemove?.(category.id)}
        color="primary"
        variant="outlined"
        size="small"
        sx={{
          height: 'auto',
          maxWidth: '100%',
          '& .MuiChip-label': {
            whiteSpace: 'normal',
            py: 0.5,
            lineHeight: 1.35,
          },
        }}
      />
    </Box>
  );
}

export default function CategoryMultiSelect({ selectedCategories = [], onChange, disabled = false }) {
  const { translate } = useTranslation();
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [labelById, setLabelById] = useState(() => new Map());
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const selectedIdsRef = useRef([]);
  const labelByIdRef = useRef(labelById);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  selectedIdsRef.current = selectedCategories.map((c) => c.id);
  labelByIdRef.current = labelById;

  useEffect(() => {
    (async () => {
      try {
        const res = await getCategoryTree();
        const tree = (res.data?.tree ?? res.data?.Tree ?? []).map(normalizeCategoryTreeNode).filter(Boolean);
        setLabelById(buildCategoryLabelMap(tree));
      } catch {
        setLabelById(new Map());
      }
    })();
  }, []);

  const resolveLabel = useCallback(
    (category) => getCategoryBreadcrumbLabel(category, labelById),
    [labelById],
  );

  const runSearch = useCallback(async (query) => {
    setLoading(true);
    try {
      const res = await getCategories({ q: query });
      const excluded = new Set(selectedIdsRef.current);
      const list = (res.data?.categories ?? res.data ?? [])
        .map(normalizeCategory)
        .filter((c) => c.id && !excluded.has(c.id))
        .map((c) => ({
          ...c,
          label: getCategoryBreadcrumbLabel(c, labelByIdRef.current),
        }));
      setOptions(list);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void runSearch(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, open, runSearch]);

  useEffect(() => {
    const excluded = new Set(selectedIdsRef.current);
    setOptions((prev) => prev.filter((opt) => !excluded.has(opt.id)));
  }, [selectedCategories]);

  useEffect(() => {
    if (labelById.size === 0) return;
    setOptions((prev) =>
      prev.map((opt) => ({
        ...opt,
        label: getCategoryBreadcrumbLabel(opt, labelById),
      })),
    );
  }, [labelById]);

  useEffect(() => {
    const handlePointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  const handlePick = (category) => {
    if (!category || selectedIdsRef.current.includes(category.id)) return;
    const label = category.label || resolveLabel(category);
    onChange?.([...selectedCategories, { ...category, label }]);
    setSearch('');
    setOpen(false);
  };

  const handleRemove = (id) => {
    onChange?.(selectedCategories.filter((c) => c.id !== id));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = selectedCategories.findIndex((c) => c.id === active.id);
    const newIndex = selectedCategories.findIndex((c) => c.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange?.(arrayMove(selectedCategories, oldIndex, newIndex));
  };

  const showEmpty = !loading && options.length === 0;

  const sortableIds = useMemo(
    () => selectedCategories.map((c) => c.id),
    [selectedCategories],
  );

  return (
    <Box ref={wrapRef}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <Stack direction="column" gap={1} sx={{ mb: 1 }}>
            {selectedCategories.map((cat, index) => (
              <Box key={cat.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, maxWidth: '100%' }}>
                <SortableCategoryChip
                  category={cat}
                  displayLabel={resolveLabel(cat)}
                  onRemove={handleRemove}
                  disabled={disabled}
                />
                {index === 0 && (
                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600, mt: 1, flexShrink: 0 }}>
                    {translate('categories.main_badge')}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
      <Paper
        variant="outlined"
        sx={{
          position: 'relative',
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: (t) => `0 0 0 1px ${t.palette.primary.main}`,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1.25 }}>
          <TextField
            fullWidth
            variant="standard"
            disabled={disabled}
            placeholder={translate('categories.search_placeholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            onFocus={() => {
              setOpen(true);
              void runSearch(search);
            }}
            InputProps={{ disableUnderline: true }}
            sx={{ '& input': { py: 1.25, fontSize: 14 } }}
          />
          {loading ? <CircularProgress size={16} sx={{ flexShrink: 0 }} /> : null}
        </Box>
        {open && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '100%',
              mt: 0.5,
              zIndex: 4,
              maxHeight: 220,
              overflow: 'auto',
            }}
          >
            {showEmpty ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                {translate('categories.no_results')}
              </Typography>
            ) : (
              options.map((opt) => {
                const optionLabel = opt.label || resolveLabel(opt);
                return (
                  <Box
                    key={opt.id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handlePick({ ...opt, label: optionLabel })}
                    sx={{ px: 1.5, py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="body2">{optionLabel}</Typography>
                    {opt.slug && optionLabel !== opt.slug ? (
                      <Typography variant="caption" color="text.secondary">{opt.slug}</Typography>
                    ) : null}
                  </Box>
                );
              })
            )}
          </Paper>
        )}
      </Paper>
    </Box>
  );
}
